import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  RefObject,
} from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import {
  updateCarStatus,
  fetchCarsDiffThanEntrega,
  approveCarStatus,
  updateEstimatedDaysInShop,
  getCar,
  updateCar,
  searchCars,
  filterCarsNonEntrega,
} from '../../../api/carsApi';
import {
  fetchCollaborators,
  fetchUsersByTypes,
  assignResponsible,
  unassignResponsible,
} from '../../../api/collaboratorsApi';
import { getPendingSLACars } from '../../../api/workSessionsApi';
import { getPendingApprovalCars } from '../../../api/notificationsApi';
import ApproveSLAModal from '../../../components/modal/approve-sla/ApproveSLAModal';

// at-65 (BUG-V32): os `setSelected*` saíram — os filtros desta tela são locais e não
// escrevem mais na fatia global da Recepção.
import {
  resetFilters,
  setSearchResults,
  setReduxCurrentPage,
  setLastSearchQuery,
  resetSearchResults,
  setSortOrderRedux,
} from '../../recepcao/recepcaoReducer';



import FilterCarModal from '../../../components/modal/filter-car/FilterCarModal';
import ApproveConfirmationModal from '../../../components/modal/approve-confirmation-chefe-de-oficina/ApproveConfirmationChefeDeOficinaModal';
import EditCarModal from '../../../components/modal/edit-car/EditCarModal';
import UpdateEstimatedDaysInShopModal from '../../../components/modal/update-estimated-days-in-shop/UpdateEstimatedDaysInShopModal';
import ChefeDeOficinaActionsModal from '../../../components/modal/chefe-de-oficina-actions-modal/ChefeDeOficinaActionsModal';
import MotivoEsteiraModal from '../../../components/modal/motivo-esteira/MotivoEsteiraModal';
import ChecklistHistoryModal from '../../../components/modal/checklist-history/ChecklistHistoryModal';
import FillFormModal from '../../formularios/modal/FillFormModal';
import { getPendingForms, getFormTemplate } from '../../../api/formsApi';
import { FormTemplate } from '../../formularios/interfaces/forms';

import { CarClass } from '../../../interfaces/Car';
import { buscarServicosPendentes, mensagemConfirmacaoEntrega } from '../../../utils/servicosPendentes';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import { useEsteiras } from '../../../hooks/useEsteiras';
import useUserType, { useIsTi } from '../../../hooks/useUserType';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { useGuardedReload } from '../../../hooks/useGuardedReload';
import { ESTEIRA } from '../../../shared/esteiras';
import { useSearchParams } from 'react-router-dom';

import SearchIcon from '../../../img/search-icon.svg';
import CarAiro from '../../../img/car-airo.svg';
import CarAssociado from '../../../img/car-associado.svg';
import CarAtual from '../../../img/car-atual.svg';
import CarMobi from '../../../img/car-mobi.svg';
import CarPerdaTotal from '../../../img/car-perda-total.svg';
import CarTerceiro from '../../../img/car-terceiro.svg';
import CarExterno from '../../../img/car-externo.svg';
import CarDiretoria from '../../../img/car-diretoria.svg';
import Moto from '../../../img/moto.svg';
import Filter from '../../../img/filter.svg';
import ArrowDown from '../../../img/arrow-down.svg';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';

import CollaboratorModal, { ColaboradorSelecionado } from '../../../components/modal/collaborator/CollaboratorModal';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
import StatusModal from '../../../components/modal/status/StatusModal';
import ActiveSessionsConflictModal from '../../../components/modal/active-sessions-conflict/ActiveSessionsConflictModal';
import { getColaboradoresElegiveis } from '../../../api/especialidadesApi';

import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import EntreguesTab from '../../../components/entregues-tab/EntreguesTab';
import { notify } from '../../../shared/feedback/notify';
import { invalidateCarsQueries } from '../../../infrastructure/react-query/invalidateCarsQueries';
import { Collaborator } from '../../../interfaces/Collaborator';
import './ChefeDeOficina.css';
import '../../recepcao/pages/Recepcao.css';
import '../../../shared/feedback/erro-de-tela.css';

/** at-134 (ADR 0065): o motivo da parada que os modais de esteira devolvem, quando o serviço fica inacabado. */
type MotivoDaParadaEnviado = { motivoParadaId?: string; pausaObservacao?: string };

/**
 * at-60: a atualização recorrente das pendências de prazo avisa com toastId fixo, para não
 * empilhar um toast por ciclo de 30s. (Até a at-107 esta mensagem cobria também a contagem de
 * notificações não lidas, que saiu desta tela junto com o sino.)
 */
const AVISO_ATUALIZACAO_INDICADORES =
  'Não foi possível atualizar os avisos de prazo desta tela. Os valores exibidos podem estar desatualizados.';

interface ChefeDeOficinaProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

interface FilterParams {
  carClass?: string | string[];
  carStatus?: string | string[];
  pendingStatus?: string | string[];
  approvalStatus?: string | string[];
  damageType?: string | string[];
}

const headers = [
  'Placa',
  'Modelo',
  'Colaboradores',
  'Classe',
  'Dias na Oficina',
  'Esteira atual',
  'Esteira pendente',
];

const defaultColumnWidths = [
  110, 170, 180, 150, 120, 150, 170,
];

const DEFAULT_SORT_BY = 'entryDate';
const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'desc';

const ChefeDeOficina: React.FC<ChefeDeOficinaProps> = ({ sidebarRef }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [cars, setCars] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'ATIVOS' | 'ENTREGUES'>('ATIVOS');
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalCars, setTotalCars] = useState(0);

  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [totalCollaborators, setTotalCollaborators] = useState(0);

  // at-45: conjunto DESEJADO de colaboradores do veículo (o modal é declarativo).
  const [selectedUsers, setSelectedUsers] = useState<ColaboradorSelecionado[]>([]);

  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela controla',
      conteudo: (
        <p>
          Aqui você conduz os veículos pela oficina: define por quais etapas cada um vai passar,
          decide para qual esteira ele avança, escolhe quem trabalha nele e aprova as horas
          apontadas pelos colaboradores. A lista mostra os veículos em atendimento; a aba
          <strong> Entregues</strong> guarda os que já saíram, para consulta, checklist e o envio da
          pesquisa de satisfação (a coluna <strong>Pesquisa</strong> mostra quais ainda não a receberam).
        </p>
      ),
    },
    {
      titulo: 'Sub-tarefas de cada serviço',
      conteudo: (
        <>
          <p>
            No <strong>planejamento de serviços</strong>, ao marcar uma etapa que tenha sub-tarefas
            cadastradas, você escolhe <strong>quais delas valem para aquele veículo</strong> — não
            precisam ser todas. As sub-tarefas são cadastradas por esteira em Administração do
            Sistema.
          </p>
          <p>
            O colaborador marca cada uma conforme termina, confirmando com a senha dele. O
            resultado aparece no bloco <strong>O que já foi feito</strong>, ao abrir o veículo, e na
            linha do tempo da Auditoria.
          </p>
          <p>
            Sub-tarefa que <strong>já foi concluída</strong> não pode ser desmarcada do
            planejamento: ela é registro do que aconteceu, não previsão. As que ficarem sem marcar
            seguem como <strong>pendentes</strong>, mesmo depois de a etapa ser concluída.
          </p>
        </>
      ),
    },
    {
      titulo: 'Colaboradores do veículo',
      conteudo: (
        <>
          <p>
            Um veículo pode ter <strong>vários colaboradores ao mesmo tempo</strong>. Em
            <strong> Definir Colaboradores</strong> você marca todos que devem trabalhar nele: ao
            salvar, quem foi marcado é adicionado e quem foi desmarcado é removido — os demais não
            são afetados. A primeira linha da lista, <strong>Não atribuir</strong>, deixa o veículo
            sem ninguém.
          </p>
          <p>
            Ao <strong>alterar</strong> ou <strong>aprovar</strong> a esteira, o padrão é
            <strong> Não atribuir</strong>: o veículo vai para a esteira nova sem colaborador, e
            quem estava nele sai (as atividades em aberto são encerradas e as horas vêm para a sua
            aprovação). Para levar a mesma equipe adiante, escolha
            <strong> Manter os colaboradores atuais</strong>. E escolher uma pessoa no campo de
            busca <strong>soma</strong> — não substitui quem continua no veículo.
          </p>
          <p>
            Só quem está no veículo consegue iniciar atividade nele. Se você remover alguém que
            está com atividade em andamento, o sistema avisa e pede confirmação antes de encerrar a
            atividade <strong>apenas dele</strong>; as horas trabalhadas são preservadas e vêm para
            a sua aprovação.
          </p>
          <p>
            O veículo precisa estar <strong>liberado pela Administração</strong> para receber
            colaborador. Esteiras que não permitem colaborador (como Entrega) não aceitam ninguém.
          </p>
          <p>
            Cada colaborador só pode trabalhar nas esteiras da sua <strong>especialidade</strong>
            (Pintor, Funileiro e assim por diante). Por isso a lista mostra <strong>apenas quem
            pode trabalhar neste veículo</strong>: quem tem a especialidade da etapa em que ele
            está e quem tem a das esteiras abertas <strong>em paralelo</strong> — estes últimos
            marcados com <strong>Em paralelo</strong> e o nome da esteira. Quem já está no veículo
            continua aparecendo, mesmo que hoje não atenda a esteira, para você poder removê-lo.
            As especialidades são configuradas pela TI, em Administração do Sistema; se a lista
            vier vazia, é ali que falta configuração.
          </p>
        </>
      ),
    },
    {
      titulo: 'Mudança de esteira',
      conteudo: (
        <>
          <p>
            O colaborador nunca move o veículo sozinho: ao finalizar, ele pode <strong>sugerir</strong>
            a próxima esteira, e a mudança fica pendente da sua aprovação. Você também pode mover o
            veículo direto, informando os dias previstos no setor (se não informar, vale o prazo
            padrão da esteira).
          </p>
          <p>
            Se houver atividade em andamento no veículo, o sistema pergunta antes de encerrá-la para
            concluir a mudança. Aprovar a esteira e aprovar as horas são <strong>decisões
            separadas</strong>: reprovar horas não cancela a mudança de esteira pendente.
          </p>
          <p>
            Nos dois caminhos, o padrão é <strong>Não atribuir</strong>: o veículo vai para a esteira
            nova <strong>sem colaborador</strong>, e quem estava nele sai. Para levar a equipe
            adiante, escolha <strong>Manter os colaboradores atuais</strong> antes de confirmar.
          </p>
          <p>
            Quando o veículo sai de uma esteira em que o serviço ainda não terminou, você escolhe o
            desfecho: <strong>Serviço concluído</strong> (a etapa se encerra) ou <strong>Deixar
            inacabado</strong>. Deixando inacabado, o serviço vira uma pendência visível e o veículo
            pode adiantar outra esteira e <strong>voltar depois para retomar de onde parou</strong> —
            útil, por exemplo, quando a peça não chegou. As atividades em andamento são encerradas de
            todo modo e as horas vão para a sua aprovação.
          </p>
          <p>
            Ao deixar inacabado, escolha o <strong>Motivo da parada</strong>: com motivos cadastrados
            em Administração do Sistema, a escolha é obrigatória. Se o colaborador já declarou um
            motivo ao finalizar, ele vem preenchido — você confirma ou troca.
          </p>
          <p>
            A escolha aparece nos <strong>dois caminhos</strong> pelos quais o veículo sai de uma
            esteira: ao <strong>alterar a esteira</strong> e ao <strong>aprovar a mudança sugerida
            pelo colaborador</strong>. Se ele já finalizou declarando que não terminou, a opção
            <strong> Deixar inacabado</strong> vem marcada — é a continuidade do que ele registrou;
            trocar para concluído sobrepõe essa declaração, e é uma decisão sua.
          </p>
          <p>
            Se a esteira nova não for da <strong>especialidade</strong> de alguém que está no
            veículo, o sistema mostra quem será afetado e pede sua confirmação. Ao confirmar, essas
            pessoas saem do veículo e as horas em aberto vêm para a sua aprovação — quem atende a
            esteira nova permanece.
          </p>
          <p>
            Se o veículo tem esteira aberta <strong>em paralelo</strong>, ela{' '}
            <strong>continua aberta</strong> quando você move a etapa para outra esteira de
            trabalho: as atividades dela não são encerradas e quem está trabalhando nela permanece
            no veículo, mesmo com "Não atribuir". A paralela só encerra quando o veículo vai para
            uma etapa de <strong>fluxo livre</strong> (Aguardando, Orçamento, Entrega) ou quando a
            própria esteira dela <strong>passa a ser a etapa</strong> do veículo.
          </p>
        </>
      ),
    },
    {
      titulo: 'Duas esteiras ao mesmo tempo (esteira em paralelo)',
      conteudo: (
        <>
          <p>
            Às vezes o veículo precisa ser trabalhado por <strong>dois setores ao mesmo tempo</strong>:
            está em Desmontagem com um montador e um mecânico precisa adiantar a mecânica. A ação{' '}
            <strong>Abrir Esteira em Paralelo</strong>, no modal do veículo, resolve isso sem mover
            o carro.
          </p>
          <p>
            O veículo <strong>continua na etapa em que está</strong>: o prazo dela não reinicia, o
            quadro não muda de coluna e o histórico de esteiras não ganha passagem nova. O que muda
            é que o outro setor passa a poder <strong>iniciar atividade</strong> na esteira aberta,
            apontando as horas <strong>nela</strong> — e a especialidade conferida é a da esteira do
            serviço, não a da etapa.
          </p>
          <p>
            A esteira precisa ser de <strong>trabalho</strong>, aceitar colaborador e estar{' '}
            <strong>liberada pela administração</strong> para este veículo — sem a liberação nenhuma
            atividade poderia começar ali, e a opção aparece desabilitada com o motivo. A etapa
            atual também precisa ser de trabalho: em Aguardando ou Orçamento não há serviço a
            paralelizar.
          </p>
          <p>
            A esteira em paralelo <strong>se fecha sozinha</strong> quando o serviço dela é
            concluído. Você também pode encerrá-la à mão no bloco{' '}
            <strong>Esteiras em paralelo</strong> — com atividade em curso, o sistema avisa quem
            será interrompido antes de confirmar, e as horas são preservadas para a sua aprovação.
          </p>
          <p>
            O selo azul ao lado da placa (por exemplo <strong>+ Mecânica</strong>) avisa que há
            serviço em paralelo. Ele é <strong>clicável em qualquer tela</strong>: abre o controle
            com o que está em curso, desde quando, quem abriu e o motivo — e, para o chefe de
            oficina, os botões de encerrar e de abrir outra.
          </p>
          <p>
            Não há limite: o mesmo veículo pode ter mais de uma esteira em paralelo. O motivo é
            opcional e serve de registro — com o tempo, mostra por que e com que frequência a
            oficina precisa trabalhar em duas esteiras ao mesmo tempo.
          </p>
        </>
      ),
    },
    {
      titulo: 'Serviço inacabado',
      conteudo: (
        <>
          <p>
            Um serviço fica <strong>inacabado</strong> quando o colaborador finaliza a atividade
            declarando que não terminou, ou quando você move o veículo escolhendo deixá-lo inacabado.
            Você recebe uma <strong>notificação</strong> quando isso acontece, e a pendência fica
            registrada no veículo — abra o veículo, ou consulte a tela de{' '}
            <strong>Tempo e SLA</strong>, para ver em qual esteira o serviço ficou parado, o{' '}
            <strong>motivo da parada</strong> escolhido e o detalhe, quando houver.
          </p>
          <p>
            O serviço volta a ser trabalhado quando o veículo <strong>retornar para aquela
            esteira</strong>: o colaborador retoma de onde parou. Enquanto a pendência existir, a
            esteira não pode ser removida do planejamento, e o aviso antes da Entrega lista o que
            ficou por terminar. Se o serviço não vai mais ser retomado, use <strong>Marcar como
            concluído</strong> no veículo — a ação fica registrada na Auditoria.
          </p>
        </>
      ),
    },
    {
      titulo: 'Aprovação de horas',
      conteudo: (
        <>
          <p>
            Toda atividade finalizada entra na sua fila como pendente. Antes de confirmar, o sistema
            mostra exatamente o que vai acontecer com o veículo e com os colaboradores naquele
            cenário — leia a confirmação, ela muda conforme o caso.
          </p>
          <p>
            Ao aprovar a última atividade <strong>concluída</strong>, o veículo vai para <strong>Aguardando
            próxima etapa</strong> e todos os colaboradores são liberados dele. Enquanto <strong>qualquer
            um</strong> ainda estiver trabalhando, o veículo não é movido — aprovar as horas de um não
            interrompe o serviço do outro. Na reprovação, só o colaborador daquelas horas sai do veículo,
            e ele vê a justificativa na tela dele.
          </p>
          <p>
            Se a atividade foi finalizada como <strong>inacabada</strong>, aprovar as horas <strong>não
            move o veículo</strong> e não desvincula ninguém: o serviço continua pendente e você decide o
            destino do veículo pelo fluxo normal de mudança de esteira.
          </p>
        </>
      ),
    },
    {
      titulo: 'Planejamento e prazos',
      conteudo: (
        <p>
          Em <strong>Serviços que o veículo vai realizar</strong> você registra as etapas previstas —
          isso orienta a Administração na hora de liberar e aparece no acompanhamento. Cada etapa mostra
          sua situação: <strong>Prevista</strong>, <strong>Em andamento</strong>, <strong>Inacabado</strong>
          ou <strong>Realizada</strong>. A tela também sinaliza prazos: dias no setor, dias na oficina e a
          previsão de entrega (90 dias contados da entrada, descontando o tempo em oficina externa). Ao
          voltar para uma esteira, o prazo daquele setor começa de novo.
          <br />
          <br />
          Ao abrir um veículo, um selo ao lado da placa avisa a situação do prazo da etapa atual:{' '}
          <strong>Em risco</strong> quando 80% do prazo já passou, <strong>Atrasado</strong> quando
          ele estourou e <strong>Sem prazo definido</strong> quando ninguém estimou a etapa. Dentro do
          prazo, não aparece selo. É a mesma régua do painel de Prazos e da tela do setor de Eventos.
        </p>
      ),
    },
    {
      titulo: 'Onde ficam seus avisos',
      conteudo: (
        <>
          <p>
            O <strong>sino</strong> fica no canto superior direito, em todas as telas: ali estão seus
            avisos de prazo de etapa e de oficina, serviço deixado inacabado, veículo liberado pela
            Administração, sessão interrompida e comentário novo num veículo. O número em cima do
            sino é quantos você ainda não leu — o mesmo número aparece no <strong>menu
            lateral</strong>, no item desta tela, e no título da aba do navegador. Aviso novo
            também aparece num quadro no canto da tela.
          </p>
          <p>
            Clicar num aviso que tem placa <strong>pesquisa aquele veículo aqui</strong>, como
            antes — a lista abre filtrada pela placa do aviso. Use <strong>Limpar tudo</strong> para
            marcar todos como lidos de uma vez.
          </p>
        </>
      ),
    },
  ];
  const [updatingCarId, setUpdatingCarId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [carToUpdateStatus, setCarToUpdateStatus] = useState<string | null>(null);
  const [activeTriggerTemplate, setActiveTriggerTemplate] = useState<FormTemplate | null>(null);
  const [activeTriggerCar, setActiveTriggerCar] = useState<{ id: string; plate: string; model: string } | null>(null);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [carToApprove, setCarToApprove] = useState<string | null>(null);
  const [initialApprovalEstimatedDays, setInitialApprovalEstimatedDays] = useState<number | undefined>(undefined);
  const [approvalCollaborators, setApprovalCollaborators] = useState<Collaborator[]>([]);
  const [selectedPendingStatus, setSelectedPendingStatus] = useState<string | undefined>(undefined);
  const [loadingConfirmationModal, setLoadingConfirmationModal] = useState(false);
  const [noResponsibleWarningOpen, setNoResponsibleWarningOpen] = useState(false);
  const [pendingApprovalAfterWarning, setPendingApprovalAfterWarning] = useState<{
    carId: string;
    estimatedDays?: number | false;
    // at-68 (BUG-V06): o desfecho declarado no modal sobrevive ao desvio "aprovar sem
    // colaborador" — senão a confirmação daquele aviso perderia a escolha do Chefe.
    desfechoServicoAtual?: 'CONCLUIDO' | 'PAUSADO';
    // at-134: o motivo da parada sobrevive ao mesmo desvio, pela mesma razão.
    motivoDaParada?: MotivoDaParadaEnviado;
  } | null>(null);

  const [pendingApprovalCars, setPendingApprovalCars] = useState<any[]>([]);
  const [loadingPendingPanel, setLoadingPendingPanel] = useState(false);
  const [pendingPanelOpen, setPendingPanelOpen] = useState(true);

  const [pendingSLACars, setPendingSLACars] = useState<any[]>([]);
  const [slaModalCar, setSlaModalCar] = useState<any | null>(null);

  const [conflictModalOpen, setConflictModalOpen] = useState<boolean>(false);
  const [conflictActiveSessions, setConflictActiveSessions] = useState<any[]>([]);
  const [onConflictConfirm, setOnConflictConfirm] = useState<() => void>(() => {});
  const [conflictActionType, setConflictActionType] = useState<'STATUS_CHANGE' | 'COLLABORATOR_CHANGE' | 'APPROVAL_CHANGE'>('STATUS_CHANGE');
  /**
   * at-62: colaboradores que serão desvinculados por não terem a especialidade da esteira destino.
   * Vêm no MESMO 409 que traz as sessões ativas, então o modal de conflito descreve os dois efeitos
   * e o Chefe aceita tudo de uma vez (em vez de encadear duas confirmações).
   */
  const [conflictInelegiveis, setConflictInelegiveis] = useState<Array<{ collaboratorId: string; collaboratorName: string | null }>>([]);
  const [conflictEsteiraDestino, setConflictEsteiraDestino] = useState<string | undefined>(undefined);
  /**
   * at-62: ids que PODEM ser adicionados ao veículo (elegíveis para a esteira atual dele).
   * `null` = sem restrição (regra inerte, ou a consulta falhou — fail-open).
   */
  const [idsElegiveis, setIdsElegiveis] = useState<string[] | null>(null);
  /**
   * at-99: quem está disponível por causa de uma esteira aberta EM PARALELO, e por qual.
   * Mapa vazio = ninguém nessa condição (o caso normal, sem paralela aberta).
   */
  const [elegiveisPorParalela, setElegiveisPorParalela] = useState<Record<string, string[]>>({});
  /**
   * A lista COMPLETA de quem pode trabalhar no veículo — vira o universo do seletor
   * (decisão do Victor, 15/09/2026). `null` = sem restrição, e aí o modal volta a listar
   * todos os colaboradores paginados pelo backend: é o caso da regra de especialidades
   * inerte e o do fail-open quando a consulta falha.
   */
  const [colaboradoresElegiveis, setColaboradoresElegiveis] = useState<
    Array<{ id: string; fullName: string | null }> | null
  >(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [isDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const pendingPanelRef = useRef<HTMLDivElement | null>(null);

  const [sortBy, setSortBy] = useState<string | undefined>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(DEFAULT_SORT_ORDER);

  const [carToEdit, setCarToEdit] = useState<any | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const [isUpdateDaysModalOpen, setIsUpdateDaysModalOpen] = useState(false);
  const [carToUpdateDays, setCarToUpdateDays] = useState<any | null>(null);

  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [selectedCarForActions, setSelectedCarForActions] = useState<string | null>(null);
  // at-89: veículo cujo motivo da pendência está sendo editado.
  const [carroParaMotivo, setCarroParaMotivo] = useState<string | null>(null);
  
  const [isChecklistHistoryModalOpen, setIsChecklistHistoryModalOpen] = useState<boolean>(false);
  const [selectedCarPlate, setSelectedCarPlate] = useState<string>('');

  const handleOpenChecklistHistoryModal = (carId: string, carPlate: string) => {
    setSelectedCarForActions(carId);
    setSelectedCarPlate(carPlate || '');
    setIsChecklistHistoryModalOpen(true);
  };

  const handleCloseChecklistHistoryModal = () => {
    setIsChecklistHistoryModalOpen(false);
    setSelectedCarForActions(null);
    setSelectedCarPlate('');
  };

  const handleOpenActionsModal = (carId: string) => {
    setSelectedCarForActions(carId);
    setActionsModalOpen(true);
  };

  const [isStatusSidebarOpen] = useState<boolean>(true);

  const [confirmUnrealizedModal, setConfirmUnrealizedModal] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    message: '',
    onConfirm: () => {},
  });

  const [confirmUnassignModal, setConfirmUnassignModal] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    message: '',
    onConfirm: () => {},
  });

  const availableClasses: CarClass[] = Object.values(CarClass);

  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);

  // at-65 (BUG-V32): as leituras de `state.recepcao.selected*` saíram — esta tela não
  // compartilha mais filtro com a Recepção.
  /**
   * at-68 (BUG-V49): o papel vem do TOKEN, não da fatia do Redux gravada no login.
   *
   * Era a segunda fonte da mesma regra: os modais desta tela sempre usaram `useUserType()`
   * (que decodifica o JWT), e a página lia `state.auth.userType`. Quando o papel muda e o
   * token é renovado, as duas divergem — e a tela passa a oferecer ações que os modais
   * escondem. Todos os usos de papel desta tela leem daqui.
   */
  const loggedUserType = useUserType();
  // TI vê a tela em modo somente leitura — sem painel de aprovações (at-25).
  /**
   * at-68 (BUG-V49): fonte ÚNICA de detecção de papel.
   *
   * A página lia `state.auth.userType` (Redux, gravado no login) enquanto os modais desta
   * mesma tela usam `useIsTi()`/`useUserType()`, que decodificam o JWT. Duas fontes para a
   * mesma regra divergem quando o papel do usuário muda e o token é renovado: a página
   * mostrava ações que os modais escondiam (ou o contrário). Agora as duas leem o token.
   */
  const isTi = useIsTi();
  const { esteiras, chaveDe, labelDe } = useEsteiras(true);
  const [actionsModalRefreshKey, setActionsModalRefreshKey] = useState(0);

  /** Usuário pode aprovar a esteira pendente do veículo? (ADMIN sempre; demais via roles APROVAR da esteira) */
  const canApproveEsteira = (car: any): boolean => {
    if (!car?.pendingStatus) return false;
    if (String(loggedUserType || '').toUpperCase() === 'ADMIN') return true;

    const pendingEsteiraId = car.pendingEsteiraId ?? car.pendingEsteira?.id;
    const esteira = esteiras.find(
      (e) => e.id === pendingEsteiraId || e.chave === car.pendingStatus
    );
    return Boolean(
      esteira?.roles?.some((r) => r.tipo === 'APROVAR' && r.role === loggedUserType)
    );
  };
  /**
   * at-65 (BUG-V32): os filtros desta tela são LOCAIS.
   *
   * O vazamento medido no estudo era entre Tela de Controle e Recepção, mas o Chefe de
   * Oficina lia e escrevia a **mesma** fatia (`state.recepcao.selected*`) — então eram
   * três telas, de papéis diferentes, compartilhando filtro sem nada na interface dizer
   * isso. O estado global segue servindo apenas a Recepção.
   *
   * ⚠️ `useMemo` com dependência vazia de propósito: `appliedFilters` é dependência de
   * efeito de carga (linha ~630), e objeto recriado a cada render disparava requisição em
   * loop. Quem troca o valor é `setAppliedFilters`, no "Aplicar Filtros".
   */
  const [appliedFilters, setAppliedFilters] = useState({
    carClass: [] as CarClass[],
    carStatus: [] as string[],
    pendingStatus: [] as string[],
    approvalStatus: undefined as string | undefined,
    damageType: [] as string[],
  });

  const totalPages = useMemo(() => {
    const pages = Math.ceil(totalCars / limit);
    return pages > 0 ? pages : 1;
  }, [totalCars, limit]);

  const displayPendingSLACars = useMemo(() => {
    return pendingSLACars;
  }, [pendingSLACars]);

  const startCarIndex = totalCars === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endCarIndex = Math.min(currentPage * limit, totalCars);

  // Valores renderizados por linha, memoizados para o hook de medição (at-43).
  // `labelDe` é estável (useCallback no useEsteiras), então as dependências fecham.
  const getColumnValues = useCallback(
    (car: any) => [
      car.plate || '-',
      car.model || '-',
      car.responsibleName || '-',
      car.carClass || '-',
      String(car.daysInShop ?? '-'),
      labelDe(car.status),
      labelDe(car.pendingStatus),
    ],
    [labelDe],
  );

  const tableRows = useMemo(() => cars.map(getColumnValues), [cars, getColumnValues]);

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: defaultColumnWidths,
    rows: tableRows,
    // A tabela de ativos só existe no DOM quando a aba "Em atendimento" está visível
    enabled: activeTab === 'ATIVOS',
  });

  const hasAnyFilter = (filters: typeof appliedFilters) =>
    Object.values(filters).some((val) => {
      if (Array.isArray(val)) return val.length > 0;
      return Boolean(val);
    });

  const loadCars = async (
    page: number,
    pageLimit: number,
    currentSortBy: string = DEFAULT_SORT_BY,
    currentSortOrder: 'asc' | 'desc' = DEFAULT_SORT_ORDER
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchCarsDiffThanEntrega(
        page,
        pageLimit,
        currentSortBy,
        currentSortOrder
      );

      setCars(response.cars);
      setTotalCars(response.total);
    } catch (err) {
      setError('Erro ao carregar os carros');
    } finally {
      setLoading(false);
    }
  };

  const loadCarsWithFilters = async (
    recepcao: FilterParams,
    page: number,
    pageLimit: number,
    currentSortBy: string = DEFAULT_SORT_BY,
    currentSortOrder: 'asc' | 'desc' = DEFAULT_SORT_ORDER
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await filterCarsNonEntrega(
        recepcao.carClass,
        recepcao.carStatus,
        recepcao.pendingStatus,
        recepcao.approvalStatus,
        recepcao.damageType,
        page,
        pageLimit,
        currentSortBy,
        currentSortOrder
      );

      setCars(response.cars);
      setTotalCars(response.total);
    } catch (err) {
      setError('Erro ao carregar os carros');
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleSearch = async (
    page: number = 1,
    currentSortBy: string = DEFAULT_SORT_BY,
    currentSortOrder: 'asc' | 'desc' = DEFAULT_SORT_ORDER
  ) => {
    setLoading(true);
    setError(null);
    handleResetFilters();

    try {
      const response = await searchCars(
        searchQuery,
        page,
        limit,
        currentSortBy,
        currentSortOrder
      );

      dispatch(
        setSearchResults({
          page,
          cars: response.cars,
          totalItems: response.total,
          totalPages: Math.ceil(response.total / limit),
        })
      );
      dispatch(setReduxCurrentPage(page));
      dispatch(setLastSearchQuery(searchQuery));

      setCars(response.cars);
      setTotalCars(response.total);
    } catch (err) {
      setError('Erro ao realizar a pesquisa');
    } finally {
      setLoading(false);
    }
  };

  const reloadCarsNow = useCallback(async () => {
    const safeSortBy = sortBy ?? DEFAULT_SORT_BY;
    const safeSortOrder = sortOrder ?? DEFAULT_SORT_ORDER;

    if (searchQuery) {
      await handleSearch(currentPage, safeSortBy, safeSortOrder);
      return;
    }

    if (hasAnyFilter(appliedFilters) || sortBy) {
      await loadCarsWithFilters(
        appliedFilters,
        currentPage,
        limit,
        safeSortBy,
        safeSortOrder
      );
      return;
    }

    await loadCars(currentPage, limit, safeSortBy, safeSortOrder);
    // 'handleSearch' e 'hasAnyFilter' são recriadas a cada render, mas leem apenas valores que
    // já estão nas dependências acima (busca, filtros, página, limite e ordenação) — não há
    // risco de dado velho. Incluí-las tornaria 'reloadCarsNow' instável, e ela é dependência do
    // efeito que registra os listeners de 'car:status-updated': os listeners seriam removidos e
    // registrados de novo em todo render, podendo perder atualizações de esteira em tempo real.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, currentPage, limit, searchQuery, sortBy, sortOrder]);

  const handleSortChange = (selectedSortBy: string, order: 'asc' | 'desc') => {
    dispatch(setSortOrderRedux({ sortBy: selectedSortBy, sortOrder: order }));
    setSortBy(selectedSortBy);
    setSortOrder(order);
    setIsFilterDropdownOpen(false);
  };

  const handleClickOutsideFilterDropdown = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsFilterDropdownOpen(false);
    }
  };

  const handleClickOutsidePendingPanel = (event: MouseEvent) => {
    if (pendingPanelRef.current && !pendingPanelRef.current.contains(event.target as Node)) {
      setPendingPanelOpen(false);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutsideFilterDropdown);
    } else {
      document.removeEventListener('mousedown', handleClickOutsideFilterDropdown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideFilterDropdown);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (pendingPanelOpen) {
      document.addEventListener('mousedown', handleClickOutsidePendingPanel);
    } else {
      document.removeEventListener('mousedown', handleClickOutsidePendingPanel);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsidePendingPanel);
    };
  }, [pendingPanelOpen]);

  // Recarrega a lista só quando filtros/página/limite/ordenação mudam de fato — a comparação
  // com o estado anterior vive no useGuardedReload (at-43); a decisão de COMO recarregar
  // (busca por texto × filtros × listagem) continua aqui.
  useGuardedReload({
    filters: appliedFilters,
    page: currentPage,
    limit,
    sortBy,
    sortOrder,
    searchQuery,
    onReload: ({ hasSortChanged }) => {
      setLoading(true);

      const safeSortBy = sortBy ?? DEFAULT_SORT_BY;
      const safeSortOrder = sortOrder ?? DEFAULT_SORT_ORDER;

      if (searchQuery) {
        handleSearch(currentPage, safeSortBy, safeSortOrder);
      } else if (hasAnyFilter(appliedFilters) || hasSortChanged) {
        loadCarsWithFilters(appliedFilters, currentPage, limit, safeSortBy, safeSortOrder);
      } else {
        loadCars(currentPage, limit, safeSortBy, safeSortOrder);
      }
    },
    onSkip: () => setLoading(false),
  });

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const openFilterModal = () => {
    setFilterModalOpen(true);
  };

  const openConfirmationModal = async (carId: string) => {
    const car = cars.find((c) => c.id === carId)
      ?? pendingApprovalCars.find((c) => c.id === carId);
    const pendingStatus = car?.pendingStatus || undefined;

    setCarToApprove(carId);
    setSelectedPendingStatus(pendingStatus);
    setInitialApprovalEstimatedDays(car?.estimatedDaysInStatus ?? undefined);
    setApprovalCollaborators([]);
    setLoadingConfirmationModal(true);
    setConfirmationModalOpen(true);

    const targetUserTypes = pendingStatus === 'ORCAMENTO' ? ['ORCAMENTO'] : ['COLABORADOR'];

    const fetchAllCollaborators = async (): Promise<Collaborator[]> => {
      const result: Collaborator[] = [];
      let page = 1;
      let total = 0;
      do {
        const response = targetUserTypes[0] === 'COLABORADOR'
          ? await fetchCollaborators(page)
          : await fetchUsersByTypes(targetUserTypes, page, 50);
        result.push(...response.users);
        total = response.total;
        page += 1;
      } while (result.length < total);
      return result;
    };

    try {
      const collaboratorsResult = await fetchAllCollaborators();
      setApprovalCollaborators(collaboratorsResult);
    } catch {
      notify.error('Erro ao carregar colaboradores para aprovação');
    } finally {
      setLoadingConfirmationModal(false);
    }
  };

  const confirmApproveStatus = async (
    input?: number | false,
    collaboratorId?: string,
    // at-68 (BUG-V06): desfecho do serviço da esteira de ORIGEM, declarado no modal.
    desfechoServicoAtual?: 'CONCLUIDO' | 'PAUSADO',
    /**
     * at-93: o Chefe escolheu "Não atribuir" (o padrão do modal) — o veículo fica sem colaborador.
     *
     * ⚠️ Antes, isto era INFERIDO de `!collaboratorId`, e por isso não existia como manter quem
     * estava no veículo ao aprovar: qualquer aprovação sem escolha desvinculava todos, enquanto o
     * modal dizia que os colaboradores continuariam. Agora "manter" é um terceiro estado real —
     * sem `collaboratorId` e sem esta flag, nada é mexido no vínculo.
     */
    deixarSemColaborador?: boolean,
    motivoDaParada?: MotivoDaParadaEnviado,
  ) => {
    if (!carToApprove) return;

    const executeApproval = () => {
      if (deixarSemColaborador) {
        setPendingApprovalAfterWarning({ carId: carToApprove, estimatedDays: input, desfechoServicoAtual, motivoDaParada });
        setConfirmationModalOpen(false);
        setNoResponsibleWarningOpen(true);
        return;
      }

      if (input === false) {
        handleApproveStatus(carToApprove, undefined, false, collaboratorId, false, false, desfechoServicoAtual, motivoDaParada);
      } else {
        handleApproveStatus(carToApprove, input, true, collaboratorId, false, false, desfechoServicoAtual, motivoDaParada);
      }
      setConfirmationModalOpen(false);
      setCarToApprove(null);
      setInitialApprovalEstimatedDays(undefined);
    };

    if (selectedPendingStatus === 'ENTREGA' && input !== false) {
      // at-46: pendência pelo estado do serviço (helper único).
      // at-60: o helper nunca rejeita — quando a consulta falha ele devolve
      // `verificacaoIndisponivel` e a mensagem abaixo passa a dizer que NÃO foi possível
      // verificar, em vez de sumir como se o veículo estivesse sem pendências.
      const pendentes = await buscarServicosPendentes(carToApprove);
      const mensagem = mensagemConfirmacaoEntrega(pendentes);
      if (mensagem) {
        setConfirmUnrealizedModal({
          isOpen: true,
          message: mensagem,
          onConfirm: () => {
            setConfirmUnrealizedModal((prev) => ({ ...prev, isOpen: false }));
            executeApproval();
          },
        });
        return;
      }
    }

    executeApproval();
  };

  const confirmNoResponsibleWarning = async () => {
    if (!pendingApprovalAfterWarning) return;
    const { carId, estimatedDays, desfechoServicoAtual, motivoDaParada } = pendingApprovalAfterWarning;
    setNoResponsibleWarningOpen(false);
    setPendingApprovalAfterWarning(null);
    setCarToApprove(null);
    setInitialApprovalEstimatedDays(undefined);
    try {
      await unassignResponsible(carId);
    } catch (error) {
      // at-60: a aprovação segue (é o que o Chefe pediu e vale por si), mas sem este aviso o
      // veículo era aprovado com o colaborador ainda vinculado e ninguém ficava sabendo.
      notify.actionError(error, {
        action: 'delete',
        subject: 'o vínculo do colaborador com este veículo',
      });
    }
    if (estimatedDays === false) {
      handleApproveStatus(carId, undefined, false, undefined, false, false, desfechoServicoAtual, motivoDaParada);
    } else {
      handleApproveStatus(carId, estimatedDays, true, undefined, false, false, desfechoServicoAtual, motivoDaParada);
    }
  };

  const loadCollaborators = async (page: number) => {
    try {
      const { users, total } = await fetchCollaborators(page);
      setCollaborators(users);
      setTotalCollaborators(total);
    } catch (err) {
      setError('Erro ao carregar colaboradores');
    }
  };

  const loadCollaboratorsPage = useCallback(async (page: number) => {
    await loadCollaborators(page);
  }, []);

  const fetchPendingApprovalCars = useCallback(async () => {
    try {
      const cars = await getPendingApprovalCars(20);
      setPendingApprovalCars(cars);
    } catch {
      // at-60: o painel vazio por falha é indistinguível de "nada pendente" — a carga também
      // se repete a cada 30s, por isso o toastId fixo.
      notify.error(
        'Não foi possível carregar os veículos aguardando aprovação. O painel pode estar incompleto. Recarregue a página; se persistir, acione a TI.',
        { toastId: 'chefe-oficina-painel-aprovacoes' },
      );
    }
  }, []);

  useEffect(() => {
    setLoadingPendingPanel(true);
    fetchPendingApprovalCars().finally(() => setLoadingPendingPanel(false));
    const interval = setInterval(fetchPendingApprovalCars, 30_000);
    return () => clearInterval(interval);
  }, [fetchPendingApprovalCars]);

  const fetchPendingSLACarsData = useCallback(async () => {
    try {
      const cars = await getPendingSLACars();
      setPendingSLACars(cars);
    } catch {
      // at-60: recorrente (30s) e some junto com a contagem de não lidas quando o backend cai —
      // um toastId só para os dois, com o mesmo texto, evita dois avisos empilhados.
      notify.warning(AVISO_ATUALIZACAO_INDICADORES, { toastId: 'chefe-oficina-indicadores' });
    }
  }, []);

  useEffect(() => {
    fetchPendingSLACarsData();
    const interval = setInterval(fetchPendingSLACarsData, 30_000);
    return () => clearInterval(interval);
  }, [fetchPendingSLACarsData]);

  /**
   * Clicar num aviso com placa pesquisa aquele veículo aqui — comportamento que o sino sempre
   * teve nesta tela.
   *
   * at-107 tirou o sino daqui e o pôs na Sidebar; a at-108 o trouxe de volta (decisão do Victor,
   * 18/09/2026), agora como componente compartilhado — o setor de Eventos tem o dele na tela de
   * Andamento dos Veículos, e a Sidebar ficou só com o número.
   */
  const pesquisarPlacaDaNotificacao = useCallback(
    async (placa: string) => {
      setSearchQuery(placa);
      dispatch(resetFilters());
      try {
        setLoading(true);
        setError(null);
        const response = await searchCars(placa, 1, limit, sortBy, sortOrder);
        dispatch(
          setSearchResults({
            page: 1,
            cars: response.cars,
            totalItems: response.total,
            totalPages: Math.ceil(response.total / limit),
          })
        );
        dispatch(setReduxCurrentPage(1));
        dispatch(setLastSearchQuery(placa));
        setCars(response.cars);
        setTotalCars(response.total);
        setCurrentPage(1);
      } catch {
        setError('Erro ao pesquisar o carro da notificação');
      } finally {
        setLoading(false);
      }
    },
    [dispatch, limit, sortBy, sortOrder],
  );

  /*
   * transversal/at-90 — o aviso de veículo aberto pelo sino do topo chega como `?placa=`. A busca
   * é a de sempre, e o parâmetro sai da URL para o F5 não refazer a busca.
   */
  const [parametrosDaUrl, setParametrosDaUrl] = useSearchParams();
  useEffect(() => {
    const placa = parametrosDaUrl.get('placa');
    if (!placa) return;
    setParametrosDaUrl({}, { replace: true });
    void pesquisarPlacaDaNotificacao(placa);
  }, [parametrosDaUrl, setParametrosDaUrl, pesquisarPlacaDaNotificacao]);

  useEffect(() => {
    const handleStatusUpdated = () => {
      reloadCarsNow();
      fetchPendingApprovalCars();
    };

    const handlePendingHours = () => {
      fetchPendingSLACarsData();
    };

    window.addEventListener('car:status-updated', handleStatusUpdated);
    window.addEventListener('work-session:pending-approval', handlePendingHours);

    return () => {
      window.removeEventListener('car:status-updated', handleStatusUpdated);
      window.removeEventListener('work-session:pending-approval', handlePendingHours);
    };
  }, [reloadCarsNow, fetchPendingApprovalCars, fetchPendingSLACarsData]);

  /**
   * at-68 (BUG-V08): os vinculados do veículo em edição vêm do BACKEND.
   *
   * Antes: `cars.find(...)` na listagem **paginada**. Veículo aberto pelo painel "Aguardando
   * aprovação" e ausente da página carregada devolvia `undefined` → `atuais = []` → o diff de
   * remoção não gerava chamada nenhuma e **a remoção falhava em silêncio**, com toast de
   * sucesso. O fallback para a listagem fica como atalho para o caso comum (veículo na
   * página), mas a verdade é a consulta.
   */
  const [vinculadosDoCarro, setVinculadosDoCarro] = useState<ColaboradorSelecionado[] | null>(null);
  const [erroAoCarregarVinculados, setErroAoCarregarVinculados] = useState(false);

  const vinculadosDoCarroEmEdicao = (): ColaboradorSelecionado[] => {
    if (vinculadosDoCarro) return vinculadosDoCarro;
    const car = cars.find((c) => c.id === updatingCarId);
    return (car?.colaboradores ?? []).map((c: any) => ({ id: c.id, fullName: c.fullName ?? null }));
  };

  // Carga dos vinculados ao abrir o modal de colaboradores.
  useEffect(() => {
    if (!modalOpen || !updatingCarId) {
      setVinculadosDoCarro(null);
      setErroAoCarregarVinculados(false);
      return;
    }

    let cancelado = false;
    getCar(updatingCarId)
      .then((carro: any) => {
        if (cancelado) return;
        setVinculadosDoCarro(
          (carro?.colaboradores ?? []).map((c: any) => ({ id: c.id, fullName: c.fullName ?? null })),
        );
        setErroAoCarregarVinculados(false);
      })
      .catch((erro) => {
        if (cancelado) return;
        // Regra da at-60: a tela permite ESCREVER sobre este dado (o save é declarativo e
        // remove quem não estiver na seleção), então falha de carga não pode virar "lista
        // vazia" — salvar em cima removeria todo mundo sem o Chefe saber.
        setVinculadosDoCarro(null);
        setErroAoCarregarVinculados(true);
        notify.errorFrom(
          erro,
          'Não foi possível carregar os colaboradores já vinculados a este veículo. Feche e abra novamente; se persistir, acione a TI.',
        );
      });

    return () => {
      cancelado = true;
    };
  }, [modalOpen, updatingCarId]);

  /**
   * Salva o conjunto desejado de colaboradores (at-45): compara com os vinculados atuais,
   * adiciona os novos e remove os que saíram — um a um, para o 409 de sessões ativas ser
   * resolvido por colaborador (a confirmação encerra só as sessões dele).
   */
  /**
   * at-68 (BUG-V10): `forceRemocaoDe` passou a ser uma LISTA.
   *
   * Com dois colaboradores em sessão ativa, o catch pegava apenas `sessoes[0]` e o retry
   * levava um único id: o segundo estourava 409 de novo e o Chefe recomeçava a operação
   * inteira. Acumulando os aceites, cada confirmação avança de verdade — e o que já foi
   * aceito não é perguntado outra vez.
   */
  const handleSaveColaboradores = async (
    forceRemocaoDe: string[] = [],
  ) => {
    if (!updatingCarId) return;

    // at-68 (BUG-V08): sem os vinculados reais, o save declarativo removeria todos.
    if (erroAoCarregarVinculados) {
      notify.error(
        'Não é possível salvar: os colaboradores já vinculados a este veículo não foram carregados, ' +
          'e salvar agora removeria quem está no veículo. Feche e abra novamente.',
      );
      return;
    }

    const atuais = vinculadosDoCarroEmEdicao();
    const desejadosIds = selectedUsers.map((u) => u.id);
    const aAdicionar = selectedUsers.filter((u) => !atuais.some((a) => a.id === u.id));
    const aRemover = atuais.filter((a) => !desejadosIds.includes(a.id));

    if (aAdicionar.length === 0 && aRemover.length === 0) {
      notify.info('Nenhuma alteração nos colaboradores do veículo');
      setModalOpen(false);
      setUpdatingCarId(null);
      return;
    }

    try {
      for (const user of aAdicionar) {
        await assignResponsible(updatingCarId, user.id);
      }

      for (const user of aRemover) {
        await unassignResponsible(updatingCarId, forceRemocaoDe.includes(user.id), user.id);
      }

      await reloadCarsNow();

      const partes: string[] = [];
      if (aAdicionar.length > 0) {
        partes.push(
          aAdicionar.length === 1
            ? `${aAdicionar[0].fullName ?? 'Colaborador'} adicionado`
            : `${aAdicionar.length} colaboradores adicionados`,
        );
      }
      if (aRemover.length > 0) {
        partes.push(
          aRemover.length === 1
            ? `${aRemover[0].fullName ?? 'Colaborador'} removido`
            : `${aRemover.length} colaboradores removidos`,
        );
      }
      notify.success(partes.join(' e '));

      setActionsModalRefreshKey((k) => k + 1);
      setModalOpen(false);
      setSelectedUsers([]);
      setUpdatingCarId(null);
      setConflictModalOpen(false);
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409 && error.response?.data?.code === 'ACTIVE_SESSIONS') {
          // Só a remoção retorna 409; o colaborador em conflito é o dono das sessões listadas.
          const sessoes = error.response.data.activeSessions || [];
          const colaboradorEmConflito = sessoes[0]?.collaboratorId;
          setConflictActiveSessions(sessoes);
          setConflictActionType('COLLABORATOR_CHANGE');
          // at-68 (BUG-V10): acumula. Sem isto, o aceite do 1º colaborador era perdido no 409
          // do 2º e a operação voltava à estaca zero a cada confirmação.
          setOnConflictConfirm(() => () =>
            handleSaveColaboradores(
              colaboradorEmConflito ? [...forceRemocaoDe, colaboradorEmConflito] : forceRemocaoDe,
            ),
          );
          setConflictModalOpen(true);
        } else {
          notify.error(
            error.response?.data?.message || 'Erro ao salvar os colaboradores. Por favor, entre em contato com o TI.',
          );
        }
      } else {
        notify.error(error.message || 'Erro desconhecido. Por favor, entre em contato com o TI.');
      }
    }
  };

  // at-45: o antigo `handleUnassignCollaborator` saiu — remover colaboradores passou a ser
  // parte do save declarativo do modal (`handleSaveColaboradores`), que trata o 409 de
  // sessões ativas por colaborador. O fluxo "aprovar sem colaborador" continua usando
  // `confirmNoResponsibleWarning`, que chama a remoção total direto.

  const handleOpenCollaboratorModal = async (carId: string) => {
    setUpdatingCarId(carId);
    // Abre já com os vinculados atuais marcados (modal declarativo).
    const car = cars.find((c) => c.id === carId);
    setSelectedUsers((car?.colaboradores ?? []).map((c: any) => ({ id: c.id, fullName: c.fullName ?? null })));
    setModalOpen(true);

    // at-62: marca no seletor quem não tem a especialidade de nenhuma esteira ATIVA do veículo.
    //
    // 🔴 at-99: o `car.id` vai junto de propósito — é ele que faz a consulta somar as esteiras
    // abertas EM PARALELO. Sem ele a tela julga só a etapa e desabilita o mecânico que o save
    // aceitaria, deixando a paralela aberta e sem ninguém a quem atribuir.
    //
    // Guarda a lista de ELEGÍVEIS (não a de bloqueados) porque o seletor é paginado — ver a doc da
    // prop `idsElegiveis` no CollaboratorModal.
    //
    // 🔴 15/09/2026 (decisão do Victor): a mesma lista passou a ser o UNIVERSO da tela, e não só
    // a marca de quem está bloqueado — o seletor mostra quem pode trabalhar na esteira do veículo
    // e nas abertas em paralelo, e mais ninguém. Emenda a at-62, que mantinha todos visíveis e
    // desabilitados: com a oficina inteira na lista, o Chefe percorria páginas de gente que não
    // podia escolher para achar as duas que podia.
    //
    // FAIL-OPEN deliberado (mesmo princípio do bloqueio de pausas da at-48b): se a consulta
    // falhar, `null` mantém o seletor completo e o backend recusa com 409 na hora de salvar.
    // Travar a tela por erro de consulta seria pior — pararia o Chefe por um problema que não é dele.
    setIdsElegiveis(null);
    setElegiveisPorParalela({});
    setColaboradoresElegiveis(null);
    if (!car?.esteiraId) return;
    try {
      const { regraAtiva, elegiveis } = await getColaboradoresElegiveis(car.esteiraId, car.id);
      // Regra inerte (nenhuma especialidade ativa) = todos podem, e a lista volta a ser a
      // completa. Restringir aqui esvaziaria o seletor numa oficina que ainda não configurou
      // especialidade nenhuma — o oposto do bootstrap inerte do ADR 0029.
      if (!regraAtiva) return;
      setIdsElegiveis(elegiveis.map((c) => c.id));
      setColaboradoresElegiveis(elegiveis.map((c) => ({ id: c.id, fullName: c.fullName ?? null })));
      // at-99: quem entra na lista pela esteira em paralelo é marcado na linha — sem isso, a
      // liberação parece vir da etapa do veículo e some sem explicação quando a paralela encerrar.
      setElegiveisPorParalela(
        Object.fromEntries(
          elegiveis
            .filter((c) => (c.viaParalela?.length ?? 0) > 0)
            .map((c) => [c.id, c.viaParalela as string[]]),
        ),
      );
    } catch (erro) {
      notify.warning(
        'Não foi possível verificar as especialidades dos colaboradores. A lista está completa; ' +
          'se algum deles não atender a esteira, o sistema avisa ao salvar.',
        { toastId: 'chefe-oficina-elegibilidade' },
      );
    }
  };

  const handleApproveStatus = async (
    carId: string,
    estimatedDaysInShop?: number,
    approve: boolean = true,
    collaboratorId?: string,
    forceCloseActiveSessions: boolean = false,
    // at-62: aceite do desvínculo de quem não tem a especialidade da esteira aprovada.
    confirmarDesvinculoInelegiveis: boolean = false,
    /**
     * at-68 (BUG-V06): o que acontece com o serviço da esteira de ORIGEM.
     *
     * Antes ia sempre `undefined`, e o backend assume `CONCLUIDO` — o que sobrescreve o
     * estado `PAUSADA` que o colaborador declarou ao finalizar. A aprovação apagava a
     * declaração de "inacabado" sem ninguém decidir isso.
     */
    desfechoServicoAtual?: 'CONCLUIDO' | 'PAUSADO',
    // at-134 (ADR 0065): por que o serviço da origem parou. Os retries abaixo o repassam.
    motivoDaParada?: MotivoDaParadaEnviado,
  ) => {
    try {
      const resultadoAprovacao = await approveCarStatus(
        carId,
        estimatedDaysInShop,
        approve,
        forceCloseActiveSessions,
        desfechoServicoAtual,
        confirmarDesvinculoInelegiveis,
        motivoDaParada,
      );

      /**
       * at-62: a atribuição vem DEPOIS da aprovação — a ordem era invertida.
       *
       * Com o assign antes, o backend validava o colaborador contra a esteira em que o veículo
       * AINDA estava, não contra a que ele ia ocupar. O select oferece quem atende o DESTINO
       * (Montagem, digamos), e a validação recusava por causa da esteira de origem (Pintura):
       * a aprovação inteira falhava citando a esteira errada, justamente para a pessoa que a
       * tela apresentou como a correta. Aprovando primeiro, a validação acontece na esteira onde
       * a pessoa vai efetivamente trabalhar — e fica igual ao `handleUpdateStatus`.
       *
       * Consequência boa de brinde: destino que não aceita colaborador (Entrega, Aguardando
       * Entrega) passa a recusar a atribuição com mensagem clara, em vez de aceitar e o backend
       * desvincular a pessoa em silêncio um instante depois.
       */
      let falhaNaAtribuicao: unknown = null;
      if (approve && collaboratorId) {
        try {
          await assignResponsible(carId, collaboratorId, forceCloseActiveSessions);
        } catch (erro) {
          falhaNaAtribuicao = erro;
        }
      }

      await invalidateCarsQueries(queryClient);
      await reloadCarsNow();
      fetchPendingApprovalCars();
      fetchPendingSLACarsData();
      setConflictModalOpen(false);

      // at-62: sucesso NÃO é incondicional — se a atribuição falhou, o resultado é parcial (a
      // esteira foi aprovada, o colaborador não entrou) e o motivo real precisa aparecer.
      if (falhaNaAtribuicao) {
        notify.errorFrom(
          falhaNaAtribuicao,
          'A esteira foi aprovada, mas o colaborador NÃO foi adicionado ao veículo.',
        );
        notify.warning(
          'Atenção: o veículo está na esteira nova SEM o colaborador escolhido. Use "Definir ' +
            'Colaboradores" para escolher alguém que atenda esta esteira.',
        );
      } else {
        notify.success(`Status ${approve ? 'aprovado' : 'rejeitado'} com sucesso`);
      }

      // at-62: efeito colateral da aprovação — quem saiu do veículo por especialidade.
      const desvinculadosNaAprovacao = resultadoAprovacao?.desvinculadosPorEspecialidade ?? [];
      if (desvinculadosNaAprovacao.length > 0) {
        const nomes = desvinculadosNaAprovacao
          .map((item: any) => item.collaboratorName ?? 'Colaborador')
          .join(', ');
        notify.warning(
          `${nomes} ${desvinculadosNaAprovacao.length === 1 ? 'saiu' : 'saíram'} do veículo por não ` +
            'ter a especialidade da esteira aprovada. As horas em aberto foram enviadas para aprovação.',
        );
      }

      setActionsModalRefreshKey((k) => k + 1);

      // CHECK FOR TRIGGERED FORMS
      if (approve) {
        try {
          const pendingForms = await getPendingForms();
          const match = pendingForms.find((item) => item.car.id === carId);
          if (match) {
            const template = await getFormTemplate(match.templateId);
            setActiveTriggerCar(match.car);
            setActiveTriggerTemplate(template);
          }
        } catch {
          // at-60: a aprovação já foi concluída — só a checagem de formulários disparados falhou.
          notify.warning(
            'A ação foi concluída, mas a atualização da tela falhou. Recarregue a página.',
            { toastId: 'chefe-oficina-formularios-pos-aprovacao' },
          );
        }
      }
    } catch (approveError: any) {
      if (axios.isAxiosError(approveError)) {
        // at-62: mesmo padrão do handleUpdateStatus — o retry leva os dois aceites.
        if (
          approveError.response?.status === 409 &&
          approveError.response?.data?.code === 'COLABORADORES_INELEGIVEIS'
        ) {
          setConflictActiveSessions(approveError.response.data.activeSessions || []);
          setConflictInelegiveis(approveError.response.data.inelegiveis || []);
          setConflictEsteiraDestino(undefined);
          setConflictActionType('APPROVAL_CHANGE');
          setOnConflictConfirm(
            () => () =>
              handleApproveStatus(carId, estimatedDaysInShop, approve, collaboratorId, true, true, desfechoServicoAtual, motivoDaParada),
          );
          setConflictModalOpen(true);
        } else if (approveError.response?.status === 409 && approveError.response?.data?.code === 'ACTIVE_SESSIONS') {
          setConflictActiveSessions(approveError.response.data.activeSessions || []);
          setConflictInelegiveis([]);
          setConflictActionType('APPROVAL_CHANGE');
          setOnConflictConfirm(() => () => handleApproveStatus(carId, estimatedDaysInShop, approve, collaboratorId, true, false, desfechoServicoAtual, motivoDaParada));
          setConflictModalOpen(true);
        } else {
          notify.error(
            approveError.response?.data?.message ||
            `Erro ao ${approve ? 'aprovar' : 'rejeitar'} status. Por favor, entre em contato com o TI.`
          );
        }
      } else {
        notify.error(approveError.message || 'Erro desconhecido. Por favor, entre em contato com o TI.');
      }
    }
  };

  const handleOpenStatusModal = (carId: string) => {
    setCarToUpdateStatus(carId);
    setStatusModalOpen(true);
  };

  const handleUpdateStatus = async (
    estimatedDaysInStatus?: number,
    collaboratorId?: string,
    // at-46: desfecho do serviço da esteira de origem, declarado no StatusModal.
    desfechoServicoAtual?: 'CONCLUIDO' | 'PAUSADO',
    force: boolean = false,
    // at-62: aceite do desvínculo de quem não tem a especialidade da esteira destino.
    confirmarDesvinculoInelegiveis: boolean = false,
    /**
     * at-93: o Chefe escolheu "Não atribuir" (o padrão do StatusModal) — o veículo vai para a
     * esteira nova sem colaborador nenhum. Nunca chega junto com `collaboratorId`.
     *
     * ⚠️ Os retries deste método (conflito de sessões e desvínculo por especialidade, abaixo)
     * PRECISAM repassar este parâmetro: sem ele, a segunda tentativa move o veículo e deixa os
     * colaboradores para trás, em silêncio. É a classe do BUG-V10 da at-68.
     */
    deixarSemColaborador: boolean = false,
    // at-134 (ADR 0065): por que o serviço da origem parou. Os retries abaixo o repassam.
    motivoDaParada?: MotivoDaParadaEnviado,
  ) => {
    if (carToUpdateStatus && selectedStatus) {
      const targetCarId = carToUpdateStatus;
      const statusToSet = selectedStatus;

      const executeUpdate = async (forceClose: boolean = false) => {
        try {
          // undefined = "usar dias padrão da esteira" (o backend aplica o slaDiasPadrao)
          if (estimatedDaysInStatus !== undefined && estimatedDaysInStatus < 1) {
            notify.warning('Informe uma estimativa de dias para alterar a esteira.');
            return;
          }

          const resultado = await updateCarStatus(
            targetCarId,
            statusToSet,
            estimatedDaysInStatus,
            forceClose,
            desfechoServicoAtual,
            confirmarDesvinculoInelegiveis,
            motivoDaParada,
          );

          // at-62: a atribuição é um SEGUNDO passo, depois de o veículo já ter mudado de esteira.
          // Quando ela falha (colaborador sem a especialidade da esteira nova, veículo não
          // liberado, esteira que não aceita colaborador), o resultado é PARCIAL — e era exatamente
          // isso que a tela escondia: o aviso genérico saía e, logo depois, o toast de sucesso da
          // mudança de esteira dizia que tudo deu certo. O Chefe concluía que o veículo tinha ido
          // para a esteira nova COM o colaborador.
          let falhaNaAtribuicao: unknown = null;
          let falhaNoDesvinculo: unknown = null;
          if (collaboratorId) {
            try {
              await assignResponsible(targetCarId, collaboratorId);
            } catch (erro) {
              falhaNaAtribuicao = erro;
            }
          } else if (deixarSemColaborador) {
            /**
             * at-93: o desvínculo vem DEPOIS da mudança de esteira, pelos mesmos dois motivos que
             * levaram a atribuição para depois (at-62, BUG-R26): se a mudança falhar, ninguém pode
             * ter sido desvinculado; e nesse ponto o próprio `updateCarStatus` já encerrou as
             * sessões do veículo, então o 409 `ACTIVE_SESSIONS` do desvínculo praticamente não
             * ocorre. `forceClose` é repassado porque, quando ele é `true`, o Chefe já aceitou
             * encerrar as sessões neste mesmo fluxo — e um 409 aqui, se vier, é reportado abaixo
             * em vez de ser engolido.
             */
            try {
              await unassignResponsible(targetCarId, forceClose);
            } catch (erro) {
              falhaNoDesvinculo = erro;
            }
          }

          await invalidateCarsQueries(queryClient);
          await reloadCarsNow();

          // CHECK FOR TRIGGERED FORMS
          try {
            const pendingForms = await getPendingForms();
            const match = pendingForms.find((item) => item.car.id === targetCarId);
            if (match) {
              const template = await getFormTemplate(match.templateId);
              setActiveTriggerCar(match.car);
              setActiveTriggerTemplate(template);
            }
          } catch {
            // at-60: a esteira já foi alterada — só a checagem de formulários disparados falhou.
            notify.warning(
              'A ação foi concluída, mas a atualização da tela falhou. Recarregue a página.',
              { toastId: 'chefe-oficina-formularios-pos-esteira' },
            );
          }

          // at-46: o toast diz o que aconteceu com o serviço da esteira de origem.
          // at-62: se a atribuição do colaborador falhou, NÃO existe toast de sucesso — o
          // resultado é parcial e o aviso carrega o motivo que veio do backend.
          const desfechoAplicado = resultado?.servicoDesfecho;
          if (falhaNaAtribuicao) {
            const destinoNome = labelDe(statusToSet) || 'a esteira selecionada';
            notify.errorFrom(
              falhaNaAtribuicao,
              `O veículo foi movido para ${destinoNome}, mas o colaborador NÃO foi adicionado.`,
            );
            notify.warning(
              `Atenção: o veículo está em ${destinoNome} SEM colaborador. Use "Definir Colaboradores" ` +
                'para escolher alguém que atenda esta esteira.',
            );
          } else if (falhaNoDesvinculo) {
            // at-93: espelho exato do caso acima — a esteira mudou, o desvínculo não aconteceu.
            // Sem isto, "Não atribuir" falhando sairia como "Esteira alterada com sucesso" e o
            // Chefe acreditaria que o veículo está livre (falso sucesso da at-60).
            const destinoNome = labelDe(statusToSet) || 'a esteira selecionada';
            notify.errorFrom(
              falhaNoDesvinculo,
              `O veículo foi movido para ${destinoNome}, mas os colaboradores NÃO foram removidos.`,
            );
            notify.warning(
              `Atenção: o veículo está em ${destinoNome} COM os colaboradores anteriores. Use ` +
                '"Definir Colaboradores" para ajustar.',
            );
          } else if (desfechoAplicado?.estado === 'PAUSADA') {
            notify.success(
              'Esteira alterada. O serviço da esteira anterior ficou registrado como inacabado e pode ser retomado quando o veículo voltar.',
            );
          } else if (desfechoAplicado?.estado === 'CONCLUIDA') {
            notify.success('Esteira alterada. O serviço da esteira anterior foi dado como concluído.');
          } else {
            notify.success('Esteira alterada com sucesso.');
          }

          // at-62: quem saiu do veículo por não ter a especialidade do destino. O aviso é
          // separado do toast de sucesso porque descreve um efeito COLATERAL da ação.
          const desvinculados = resultado?.desvinculadosPorEspecialidade ?? [];
          if (desvinculados.length > 0) {
            const nomes = desvinculados
              .map((item: any) => item.collaboratorName ?? 'Colaborador')
              .join(', ');
            notify.warning(
              `${nomes} ${desvinculados.length === 1 ? 'saiu' : 'saíram'} do veículo por não ter a ` +
                'especialidade da esteira nova. As horas em aberto foram enviadas para aprovação.',
            );
          }
          setStatusModalOpen(false);
          setSelectedStatus(null);
          setCarToUpdateStatus(null);
          setConflictModalOpen(false);
          // Atualiza o modal de ações que pode estar aberto por baixo (empilhado)
          setActionsModalRefreshKey((k) => k + 1);
        } catch (updateError: any) {
          if (axios.isAxiosError(updateError)) {
            // at-62: colaborador sem a especialidade do destino. O retry leva os DOIS aceites —
            // o payload já descreveu as sessões que serão encerradas, então pedir o force-close
            // num segundo modal seria perguntar duas vezes a mesma coisa.
            if (
              updateError.response?.status === 409 &&
              updateError.response?.data?.code === 'COLABORADORES_INELEGIVEIS'
            ) {
              setConflictActiveSessions(updateError.response.data.activeSessions || []);
              setConflictInelegiveis(updateError.response.data.inelegiveis || []);
              setConflictEsteiraDestino(labelDe(statusToSet) || undefined);
              setConflictActionType('STATUS_CHANGE');
              // at-93: o retry leva também a escolha "Não atribuir" — sem ela, a 2ª tentativa
              // moveria o veículo e deixaria os colaboradores para trás, sem avisar ninguém.
              setOnConflictConfirm(
                () => () =>
                  handleUpdateStatus(
                    estimatedDaysInStatus,
                    collaboratorId,
                    desfechoServicoAtual,
                    true,
                    true,
                    deixarSemColaborador,
                    motivoDaParada,
                  ),
              );
              setConflictModalOpen(true);
            } else if (updateError.response?.status === 409 && updateError.response?.data?.code === 'ACTIVE_SESSIONS') {
              setConflictActiveSessions(updateError.response.data.activeSessions || []);
              setConflictInelegiveis([]);
              setConflictActionType('STATUS_CHANGE');
              setOnConflictConfirm(
                () => () =>
                  handleUpdateStatus(
                    estimatedDaysInStatus,
                    collaboratorId,
                    desfechoServicoAtual,
                    true,
                    false,
                    deixarSemColaborador,
                    motivoDaParada,
                  ),
              );
              setConflictModalOpen(true);
            } else {
              notify.error(
                updateError.response?.data?.message || 'Erro ao atualizar status. Por favor, entre em contato com o TI.'
              );
            }
          } else {
            notify.error(updateError.message || 'Erro desconhecido. Por favor, entre em contato com o TI.');
          }
        }
      };

      if (chaveDe(statusToSet) === ESTEIRA.ENTREGA && !force) {
        // at-46: usa o estado do serviço (helper único), separando "ficou inacabado" de
        // "nunca foi realizado" — a derivação antiga por período APPROVED dava serviço
        // pausado como concluído e não avisava nada.
        // at-60: falha na consulta não rejeita nem passa por "sem pendências" — vira
        // `verificacaoIndisponivel`, e a mensagem de confirmação abaixo declara isso ao Chefe.
        const pendentes = await buscarServicosPendentes(targetCarId, {
          ignorarEsteiraIds: [statusToSet],
        });
        const mensagem = mensagemConfirmacaoEntrega(pendentes);
        if (mensagem) {
          setConfirmUnrealizedModal({
            isOpen: true,
            message: mensagem,
            onConfirm: () => {
              setConfirmUnrealizedModal((prev) => ({ ...prev, isOpen: false }));
              executeUpdate(force);
            },
          });
          return;
        }
      }

      executeUpdate(force);
    }
  };

  const handleApplyFilters = async (filters: any) => {
    dispatch(resetSearchResults());
    // at-65 (BUG-V32): filtro fica nesta tela, não no slice compartilhado da Recepção.
    setAppliedFilters({
      carClass: filters.carClass || [],
      carStatus: filters.carStatus || [],
      pendingStatus: filters.pendingStatus || [],
      approvalStatus: filters.approvalStatus || undefined,
      damageType: filters.damageType || [],
    });

    setCurrentPage(1);
    setLoading(true);

    try {
      const response = await filterCarsNonEntrega(
        filters.carClass,
        filters.carStatus,
        filters.pendingStatus,
        filters.approvalStatus,
        filters.damageType,
        1,
        limit,
        sortBy,
        sortOrder
      );

      setCars(response.cars);
      setTotalCars(response.total);
    } catch (filterError) {
      setError('Erro ao aplicar os filtros');
    } finally {
      setLoading(false);
    }

    setFilterModalOpen(false);
  };

  const handleOpenEditModal = async (carId: string) => {
    try {
      const details = await getCar(carId);
      setCarToEdit(details);
      setIsEditModalOpen(true);
    } catch (editError) {
      setError('Erro ao carregar detalhes do carro para edição');
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCarToEdit(null);
  };

  const handleOpenUpdateDaysModal = (car: any) => {
    setCarToUpdateDays(car);
    setIsUpdateDaysModalOpen(true);
  };

  const handleConfirmUpdateDays = async (days: number) => {
    if (!carToUpdateDays) return;

    try {
      await updateEstimatedDaysInShop(carToUpdateDays.id, days);
      await reloadCarsNow();

      notify.success('Previsão de dias atualizada com sucesso');
      setActionsModalRefreshKey((k) => k + 1);
    } catch (updateDaysError) {
      if (axios.isAxiosError(updateDaysError)) {
        notify.error(
          updateDaysError.response?.data?.message || 'Erro ao atualizar previsão'
        );
      } else {
        notify.error('Erro desconhecido');
      }
    } finally {
      setIsUpdateDaysModalOpen(false);
      setCarToUpdateDays(null);
    }
  };

  const getCarClassIcon = (carClass?: string) => {
    if (carClass === 'Airo Clube') {
      return <img src={CarAiro} alt="Car Airo" width="15" height="15" />;
    }
    if (carClass === 'Associado') {
      return <img src={CarAssociado} alt="Car Associado" width="15" height="15" />;
    }
    if (carClass === 'Carros Atual') {
      return <img src={CarAtual} alt="Car Atual" width="15" height="15" />;
    }
    if (carClass === 'Mobi') {
      return <img src={CarMobi} alt="Car Mobi" width="15" height="15" />;
    }
    if (carClass === 'Perda Total') {
      return <img src={CarPerdaTotal} alt="Car Perda Total" width="15" height="15" />;
    }
    if (carClass === 'Terceiro') {
      return <img src={CarTerceiro} alt="Car Terceiro" width="15" height="15" />;
    }
    if (carClass === 'Carros Externos') {
      return <img src={CarExterno} alt="Car Externo" width="15" height="15" />;
    }
    if (carClass === 'Moto') {
      return <img src={Moto} alt="Moto" width="15" height="15" />;
    }
    if (carClass === 'Diretoria') {
      return <img src={CarDiretoria} alt="Car Diretoria" width="15" height="15" />;
    }

    return null;
  };

  const renderPagination = () => {
    if (totalPages === 1) {
      return (
        <div key={1} className="page-number active">
          1
          <div className="Ellipse14" />
        </div>
      );
    }

    const pages = [];
    const maxVisiblePages = 10;

    pages.push(
      <div
        key={1}
        className={`page-number ${currentPage === 1 ? 'active' : ''}`}
        onClick={() => setCurrentPage(1)}
      >
        1
        {currentPage === 1 && <div className="Ellipse14" />}
      </div>
    );

    if (currentPage > maxVisiblePages - 4) {
      pages.push(
        <span key="left-ellipsis" className="ellipsis">
          ...
        </span>
      );
    }

    const startPage = Math.max(
      2,
      Math.min(
        currentPage - Math.floor((maxVisiblePages - 2) / 2),
        totalPages - (maxVisiblePages - 2)
      )
    );

    const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <div
          key={i}
          className={`page-number ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
          {currentPage === i && <div className="Ellipse14" />}
        </div>
      );
    }

    if (endPage < totalPages - 1) {
      pages.push(
        <span key="right-ellipsis" className="ellipsis">
          ...
        </span>
      );
    }

    pages.push(
      <div
        key={totalPages}
        className={`page-number ${currentPage === totalPages ? 'active' : ''}`}
        onClick={() => setCurrentPage(totalPages)}
      >
        {totalPages}
        {currentPage === totalPages && <div className="Ellipse14" />}
      </div>
    );

    return pages;
  };

  const formatWaitTime = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  const showAtivosLoading = activeTab === 'ATIVOS' && loading;

  const tabsRow = (
    <div className="recepcao-tabs">
      <button
        type="button"
        className={`recepcao-tab ${activeTab === 'ATIVOS' ? 'active' : ''}`}
        onClick={() => setActiveTab('ATIVOS')}
      >
        Em atendimento
      </button>
      <button
        type="button"
        className={`recepcao-tab ${activeTab === 'ENTREGUES' ? 'active' : ''}`}
        onClick={() => setActiveTab('ENTREGUES')}
      >
        Entregues
      </button>
    </div>
  );

  return (
    <div
      className="Frame1000004012"
      style={{
        height: showAtivosLoading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: showAtivosLoading ? 'center' : 'space-between',
        alignItems: showAtivosLoading ? 'center' : 'flex-start',
        backgroundColor: showAtivosLoading ? '#808080' : 'transparent',
      }}
    >
      {showAtivosLoading && <FullScreenLoader />}
      {activeTab === 'ENTREGUES' && (
        <div className="ConteDo chefe-conte-do">
          <EntreguesTab sidebarRef={sidebarRef} tabs={tabsRow} title="Chefe de Oficina" />
        </div>
      )}

      {activeTab === 'ATIVOS' && !loading && (
        <div className="ConteDo chefe-conte-do">
          {/* at-65: a faixa fica DENTRO do conteúdo; o `!error` apagava a tela inteira. */}
          {error && (
            <div className="erro-de-tela" role="alert">
              {error}
            </div>
          )}
          <div className="chefe-de-oficina-header-row">
            <div className="ChefeDeOficina" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Chefe de Oficina
              <button
                type="button"
                className="help-circle-btn"
                title="Como Funciona"
                onClick={() => setIsHelpOpen(true)}
              >
                ?
              </button>
            </div>
              <div className="chefe-search-bar">
                <div className="ItemSearch" onClick={() => handleSearch()}>
                  <img
                    className="imgIcon"
                    src={SearchIcon}
                    alt="Ícone de busca"
                    width="20"
                    height="20"
                  />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Pesquise por placa, responsável ou nº do evento"
                  className="chefe-search-input"
                />
              </div>

              {/*
                transversal/at-90: o sino desta tela saiu — o do topo vale para o sistema todo, e o
                clique num aviso com placa chega aqui como `?placa=`, lido abaixo.
              */}

              {!isTi && (displayPendingSLACars.length > 0 || pendingApprovalCars.length > 0) && (
                <div className="pending-approvals-header-wrapper" ref={pendingPanelRef} style={{ position: 'relative' }}>
                  <div
                    className="pending-approvals-header"
                    onClick={() => setPendingPanelOpen((o) => !o)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 16px',
                      background: 'var(--tema-tinta-laranja, #FFF3E7)',
                      border: '1.5px solid var(--tema-borda-laranja, #FFD8B3)',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <div className="pending-approvals-header-left">
                      <span className="pending-approvals-dot" />
                      <span className="pending-approvals-title" style={{ color: '#ea5d0e' }}>Aguardando aprovação</span>
                      <span className="pending-approvals-count">{displayPendingSLACars.length + pendingApprovalCars.length}</span>
                    </div>
                    <span className={`pending-approvals-chevron ${pendingPanelOpen ? 'open' : ''}`}>▼</span>
                  </div>
 
                  {pendingPanelOpen && (
                    <div
                      className="pending-approvals-body"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        left: 0,
                        width: 'min(540px, calc(100vw - 40px))',
                        background: 'var(--tema-superficie, white)',
                        border: '1px solid var(--tema-linha-2, #CACACA)',
                        borderRadius: '12px',
                        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
                        zIndex: 100,
                        maxHeight: '400px',
                        overflowY: 'auto',
                        padding: '8px 0',
                      }}
                    >
                      {loadingPendingPanel ? (
                        <div className="pending-approvals-loading">Carregando...</div>
                      ) : (
                        <>
                          {pendingApprovalCars.map((car) => (
                            <div
                              key={car.id}
                              className="pending-approval-row"
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '90px 1fr 60px auto',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '10px 18px',
                              }}
                            >
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <span className="pending-approval-plate">{car.plate}</span>
                                <span style={{ fontSize: '10px', fontWeight: 600, color: '#ea5d0e', background: 'var(--tema-tinta-laranja, #FFF3E7)', border: '1px solid var(--tema-borda-laranja, #FFD8B3)', borderRadius: '4px', padding: '1px 5px', alignSelf: 'flex-start' }}>STATUS</span>
                              </div>
 
                              <div className="pending-approval-flow">
                                <span className="pending-approval-flow-from">{labelDe(car.status)}</span>
                                <span className="pending-approval-flow-arrow">→</span>
                                <span className="pending-approval-flow-to">{labelDe(car.pendingStatus)}</span>
                              </div>
 
                              <span className="pending-approval-time">
                                {formatWaitTime(car.pendingStatusUpdatedAt)}
                              </span>
 
                              <button
                                className="pending-approval-btn"
                                onClick={() => openConfirmationModal(car.id)}
                              >
                                Decidir
                              </button>
                            </div>
                          ))}
 
                          {displayPendingSLACars.length > 0 && pendingApprovalCars.length > 0 && (
                            <div style={{ height: '1px', background: 'var(--tema-fundo, #F0F0F0)', margin: '4px 18px' }} />
                          )}
 
                          {displayPendingSLACars.map((car) => (
                            <div
                              key={car.carId}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '90px 1fr auto',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '10px 18px',
                              }}
                            >
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <span className="pending-approval-plate">{car.carPlate}</span>
                                <span style={{ fontSize: '10px', fontWeight: 600, color: '#ea5d0e', background: 'var(--tema-tinta-laranja, #FFF3E7)', border: '1px solid var(--tema-borda-laranja, #FFD8B3)', borderRadius: '4px', padding: '1px 5px', alignSelf: 'flex-start' }}>HORAS</span>
                              </div>
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--tema-texto, #222)' }}>{car.carModel}</div>
                                <div style={{ fontSize: '12px', color: 'var(--tema-texto-2, #666)' }}>
                                  {car.pendingSessionsCount} sessão{car.pendingSessionsCount !== 1 ? 'ões' : ''} pendente{car.pendingSessionsCount !== 1 ? 's' : ''}
                                </div>
                              </div>
                              <button
                                className="pending-approval-btn"
                                onClick={() => setSlaModalCar(car)}
                              >
                                Decidir
                              </button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

          </div>

          {tabsRow}

          <div className="VisualizarOrdenar">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              <div className="CtaBig">
                <div className="Frame1000003987">
                  <div className="ComponentName">Itens por página:</div>
                  <select
                    id="limit"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="ComponentNameImgIcon"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <div className="CtaBig MouseHover" onClick={openFilterModal}>
                <div className="Frame1000003987">
                  <img
                    src={Filter}
                    alt="Filtro"
                    width="19.8"
                    height="19.8"
                  />
                  <div className="ComponentName">Filtrar</div>
                </div>
              </div>

              <div className="CtaBig MouseHover" onClick={() => setIsFilterDropdownOpen(!isDropdownOpen)}>
                <div className="Frame1000003987">
                  <img
                    src={ArrowDown}
                    alt="Ordenar"
                    width="12.8"
                    height="12.8"
                  />
                  <div className="ComponentName">Ordenar por</div>
                </div>

                {isDropdownOpen && (
                  <div className="DropdownMenu" ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('plate', 'asc');
                      }}
                    >
                      Placa
                    </div>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('entryDate', 'desc');
                      }}
                    >
                      Data de entrada mais recente
                    </div>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('entryDate', 'asc');
                      }}
                    >
                      Data de entrada mais antiga
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="QuantidadeDeCarros">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
              }}
            >
              <div className="CtaQuantidadeDeCarros">
                <div className="Frame1000003987">
                  <div className="ComponentName">
                    {totalCars === 0
                      ? '0 carros'
                      : `${startCarIndex}-${endCarIndex} de ${totalCars} carros`}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              width: '100%',
              paddingTop: 30,
              background: 'var(--tema-superficie, white)',
              borderRadius: 16,
              border: '1px var(--tema-linha-2, #CACACA) solid',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex',
              marginBottom: 20,
            }}
          >
            <div className="TabelaScroll" ref={tableScrollRef}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: tableColumns,
                  width: tableWidth,
                  minWidth: tableWidth,
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {headers.map((header, index) => (
                        <div
                          key={header}
                          className="Opcoes"
                          ref={(el) => {
                            headerRefs.current[index] = el;
                          }}
                        >
                          {header}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {cars.length === 0 ? (
                  <div className="Tabela">
                    <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                      <div className="Data">Nenhum carro encontrado.</div>
                    </div>
                  </div>
                ) : (
                  cars.map((car) => (
                    <div key={car.id} className="Tabela">
                      <div className="Item">
                        <div
                          className="Data"
                          onClick={() => handleOpenActionsModal(car.id)}
                          style={{
                            cursor: 'pointer',
                            color: '#EA5D0E',
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                          }}
                        >
                          {car.plate || '-'}
                        </div>
                      </div>

                      <div className="Item">
                        <div className="Data">{car.model || '-'}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">{car.responsibleName || '-'}</div>
                      </div>

                      <div className="Item">
                        <div
                          className="Data"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          {getCarClassIcon(car.carClass)}
                          <span>{car.carClass || '-'}</span>
                        </div>
                      </div>

                      <div className="Item">
                        <div className="Data">{car.daysInShop ?? '-'}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">
                          <span>{labelDe(car.status)}</span>
                        </div>
                      </div>

                      <div className="Item">
                        <div className="ChefeDeOficinaPendingCell">
                          <span
                            className="Data"
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                              display: 'inline-block',
                            }}
                          >
                            {labelDe(car.pendingStatus)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="PaginaO">
              <div
                className={`Frame1000003950 ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                  }
                }}
                style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                <img
                  className="imgIcon"
                  src={currentPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
                  alt="Seta para Esquerda"
                  width="24"
                  height="24"
                />
                <div
                  className="Anterior"
                  style={{ color: currentPage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
                >
                  Anterior
                </div>
              </div>

              <div className="Group21">
                <div className="Group20">{renderPagination()}</div>
              </div>

              <div
                className={`Frame1000003949 ${currentPage === totalPages ? 'disabled' : ''
                  }`}
                onClick={() => {
                  if (currentPage < totalPages) {
                    setCurrentPage(currentPage + 1);
                  }
                }}
                style={{
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                <div
                  className="PrXima"
                  style={{ color: currentPage === totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
                >
                  Próxima
                </div>
                <img
                  className="imgIcon"
                  src={currentPage === totalPages ? ArrowRightGray : ArrowRightOrange}
                  alt="Seta para Direita"
                  width="24"
                  height="24"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <CollaboratorModal
          isOpen={modalOpen}
          collaborators={collaborators}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          vinculadosAtuais={vinculadosDoCarroEmEdicao()}
          onConfirm={() => handleSaveColaboradores()}
          onCancel={() => {
            setModalOpen(false);
            setSelectedUsers([]);
            setUpdatingCarId(null);
          }}
          totalCollaborators={totalCollaborators}
          loadPage={loadCollaboratorsPage}
          sidebarRef={sidebarRef}
          isSidebarOpen={isStatusSidebarOpen}
          idsElegiveis={idsElegiveis}
          elegiveisPorParalela={elegiveisPorParalela}
          colaboradoresElegiveis={colaboradoresElegiveis}
        />
      )}

      <EditCarModal
        isOpen={isEditModalOpen}
        onCancel={handleCloseEditModal}
        carDetails={carToEdit}
        onUpdate={async (id: string, data: any) => {
          try {
            await updateCar(id, data);
            handleCloseEditModal();
            await reloadCarsNow();
          } catch (updateError) {
            setError('Erro ao atualizar carro');
          }
        }}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
      />

      <StatusModal
        isOpen={statusModalOpen}
        carId={carToUpdateStatus}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        onConfirm={(days, collaboratorId, desfechoServicoAtual, deixarSemColaborador, motivoDaParada) =>
          handleUpdateStatus(
            days,
            collaboratorId,
            desfechoServicoAtual,
            false,
            false,
            deixarSemColaborador,
            motivoDaParada,
          )
        }
        onCancel={() => {
          setStatusModalOpen(false);
          setSelectedStatus(null);
          setCarToUpdateStatus(null);
        }}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
        isChefe
      />

      <ActiveSessionsConflictModal
        isOpen={conflictModalOpen}
        activeSessions={conflictActiveSessions}
        actionType={conflictActionType}
        inelegiveis={conflictInelegiveis}
        esteiraDestinoNome={conflictEsteiraDestino}
        onConfirm={onConflictConfirm}
        onCancel={() => {
          setConflictModalOpen(false);
          setConflictActiveSessions([]);
          setConflictInelegiveis([]);
          setConflictEsteiraDestino(undefined);
        }}
      />

      <ApproveConfirmationModal
        isOpen={confirmationModalOpen}
        isLoading={loadingConfirmationModal}
        // at-99: o seletor de colaborador soma as esteiras abertas em paralelo, que continuam
        // vigentes depois da aprovação — sem o id do veículo ele julgaria só o destino.
        carId={carToApprove}
        message={(() => {
          const car = cars.find((c) => c.id === carToApprove)
            ?? pendingApprovalCars.find((c) => c.id === carToApprove);
          const destino = car?.pendingEsteira?.nome || car?.pendingStatusLabel || car?.pendingStatus;
          return destino
            ? `Tem certeza de que deseja aprovar a mudança deste veículo para a esteira "${destino}"?`
            : 'Tem certeza de que deseja aprovar a esteira deste veículo?';
        })()}
        onConfirm={(days, collaboratorId, desfechoServicoAtual, deixarSemColaborador, motivoDaParada) =>
          confirmApproveStatus(days, collaboratorId, desfechoServicoAtual, deixarSemColaborador, motivoDaParada)
        }
        initialEstimatedDaysInShop={initialApprovalEstimatedDays}
        collaborators={approvalCollaborators}
        // at-62: quem JÁ está no veículo, para o modal dizer quem continua e quem sai ao aprovar.
        colaboradoresDoVeiculo={(() => {
          const car = cars.find((c) => c.id === carToApprove)
            ?? pendingApprovalCars.find((c) => c.id === carToApprove);
          return (car?.colaboradores ?? []).map((c: any) => ({ id: c.id, fullName: c.fullName ?? null }));
        })()}
        /**
         * at-68 (BUG-V06): serviço em aberto na esteira em que o veículo ESTÁ.
         *
         * `servicosInacabados` (at-46, na projeção desde a at-65) traz os serviços em aberto
         * do carro; interessa o da esteira atual, que é a de ORIGEM da mudança aprovada.
         */
        servicoAtualEmAberto={(() => {
          const car = cars.find((c) => c.id === carToApprove)
            ?? pendingApprovalCars.find((c) => c.id === carToApprove);
          if (!car?.esteiraId) return null;
          const emAberto = (car.servicosInacabados ?? []).find(
            (s: any) => s.esteiraId === car.esteiraId,
          );
          return emAberto ?? null;
        })()}
        onCancel={() => {
          setConfirmationModalOpen(false);
          setCarToApprove(null);
          setSelectedPendingStatus(undefined);
          setInitialApprovalEstimatedDays(undefined);
          setApprovalCollaborators([]);
          setLoadingConfirmationModal(false);
        }}
        onReject={(collabId, deixarSemColaborador) =>
          confirmApproveStatus(false, collabId, undefined, deixarSemColaborador)
        }
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
        selectedStatus={selectedPendingStatus}
      />

      {noResponsibleWarningOpen && (
        <div className="chefe-approve-overlay">
          <div className="chefe-approve-modal">
            <div className="chefe-approve-header">
              <h3 className="chefe-approve-title">Atenção</h3>
            </div>
            <div className="chefe-approve-body">
              <p style={{ fontSize: '14px', color: 'var(--tema-texto-2, #374151)', margin: 0 }}>
                {/* at-68 (BUG-V07): o aviso dizia só "ficará sem responsável" — mas a ação
                    remove TODOS os vinculados (o veículo pode ter vários desde o at-45/ADR 0020),
                    e o Chefe não sabia quantos nem quem. Frases em gênero neutro: o sistema não
                    guarda o gênero de quem está no cadastro (regra da at-62). */}
                {/* at-93: o gatilho deixou de ser "nenhum colaborador foi selecionado" (que era
                    inferência) e passou a ser a escolha explícita "Não atribuir" — o texto
                    acompanha, senão descreveria uma omissão do usuário em vez da decisão dele. */}
                {(() => {
                  const car = cars.find((c) => c.id === pendingApprovalAfterWarning?.carId)
                    ?? pendingApprovalCars.find((c) => c.id === pendingApprovalAfterWarning?.carId);
                  const vinculados = (car?.colaboradores ?? []) as Array<{ id: string; fullName?: string | null }>;
                  if (vinculados.length === 0) {
                    return 'Você escolheu "Não atribuir", e o veículo já está sem colaborador vinculado. Deseja continuar?';
                  }
                  const nomes = vinculados.map((c) => c.fullName ?? 'Colaborador').join(', ');
                  return vinculados.length === 1
                    ? `Você escolheu "Não atribuir": ${nomes} sai do veículo e ele fica sem colaborador. Deseja continuar?`
                    : `Você escolheu "Não atribuir": os ${vinculados.length} colaboradores vinculados (${nomes}) saem do veículo e ele fica sem colaborador. Deseja continuar?`;
                })()}
              </p>
            </div>
            <div className="chefe-approve-footer">
              <button
                className="chefe-approve-btn cancel"
                type="button"
                onClick={() => {
                  setNoResponsibleWarningOpen(false);
                  setPendingApprovalAfterWarning(null);
                }}
              >
                Cancelar
              </button>
              <button
                className="chefe-approve-btn confirm"
                type="button"
                onClick={confirmNoResponsibleWarning}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* at-65 (BUG-V43): montagem condicional + filtros em vigor. */}
      {filterModalOpen && (
        <FilterCarModal
          onCancel={() => setFilterModalOpen(false)}
          onApplyFilters={handleApplyFilters}
          availableClasses={availableClasses}
          sidebarRef={sidebarRef}
          isSidebarOpen={isStatusSidebarOpen}
          filtrosAplicados={{
            carClass: appliedFilters.carClass,
            carStatus: appliedFilters.carStatus,
            pendingStatus: appliedFilters.pendingStatus,
          }}
        />
      )}

      <UpdateEstimatedDaysInShopModal
        isOpen={isUpdateDaysModalOpen}
        currentValue={carToUpdateDays?.estimatedDaysInStatus}
        onCancel={() => {
          setIsUpdateDaysModalOpen(false);
          setCarToUpdateDays(null);
        }}
        onConfirm={handleConfirmUpdateDays}
        sidebarRef={sidebarRef}
      />

      <ChefeDeOficinaActionsModal
        isOpen={actionsModalOpen}
        carId={selectedCarForActions}
        onCancel={() => setActionsModalOpen(false)}
        canApproveEsteira={canApproveEsteira}
        onApproveEsteira={(id) => {
          // O modal de ações permanece aberto (com blur) por baixo do modal de aprovação
          openConfirmationModal(id);
        }}
        pendingHorasCar={
          selectedCarForActions
            ? displayPendingSLACars.find((p: any) => p.carId === selectedCarForActions) ?? null
            : null
        }
        onApproveHoras={(slaCar) => {
          setSlaModalCar(slaCar);
        }}
        suppressClickOutside={confirmationModalOpen || slaModalCar !== null || modalOpen || isUpdateDaysModalOpen || statusModalOpen}
        refreshKey={actionsModalRefreshKey}
        onOpenEditModal={(carId) => {
          setActionsModalOpen(false);
          handleOpenEditModal(carId);
        }}
        onOpenCollaboratorModal={(carId) => {
          // Empilha sobre o modal de ações (permanece aberto com blur por baixo)
          handleOpenCollaboratorModal(carId);
        }}
        onOpenUpdateDaysModal={(car) => {
          // Empilha sobre o modal de ações (permanece aberto com blur por baixo)
          handleOpenUpdateDaysModal(car);
        }}
        onOpenStatusModal={(carId) => {
          // Empilha sobre o modal de ações (permanece aberto com blur por baixo)
          handleOpenStatusModal(carId);
        }}
        onOpenChecklistHistory={(carId, carPlate) => {
          setActionsModalOpen(false);
          handleOpenChecklistHistoryModal(carId, carPlate);
        }}
        /* at-89: fecha o modal de ações antes de abrir — o modal do motivo é renderizado FORA
           do `ref` deste, então mantê-lo aberto faria o primeiro clique dentro dele derrubar o
           de trás (a armadilha do `travarCliqueFora`). */
        onEditarMotivo={(carId) => {
          setActionsModalOpen(false);
          setCarroParaMotivo(carId);
        }}
        sidebarRef={sidebarRef}
      />

      {/* at-89: edição do motivo da pendência da esteira atual. */}
      <MotivoEsteiraModal
        isOpen={Boolean(carroParaMotivo)}
        carId={carroParaMotivo}
        esteiraAtual={cars.find((c: any) => c?.id === carroParaMotivo)?.status ?? null}
        onCancel={() => setCarroParaMotivo(null)}
        onSaved={() => setActionsModalRefreshKey((k) => k + 1)}
        sidebarRef={sidebarRef}
      />

      <ChecklistHistoryModal
        isOpen={isChecklistHistoryModalOpen}
        onCancel={handleCloseChecklistHistoryModal}
        carId={selectedCarForActions || ''}
        carPlate={selectedCarPlate}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
        // at-65 (BUG-V11): tela OPERACIONAL — preserva o caminho de edição de checklist que
        // o Chefe já tinha. A permissão do usuário continua decidindo por cima disto.
        permitirEdicao
      />

      <FillFormModal
        isOpen={!!activeTriggerTemplate}
        template={activeTriggerTemplate}
        preselectedCar={activeTriggerCar}
        onClose={() => {
          setActiveTriggerTemplate(null);
          setActiveTriggerCar(null);
        }}
        onSubmitted={() => {
          setActiveTriggerTemplate(null);
          setActiveTriggerCar(null);
        }}
      />

      {confirmUnrealizedModal.isOpen && (
        <ConfirmModal
          isOpen={confirmUnrealizedModal.isOpen}
          message={confirmUnrealizedModal.message}
          confirmLabel="Sim, tenho certeza"
          onConfirm={confirmUnrealizedModal.onConfirm}
          onCancel={() => setConfirmUnrealizedModal((prev) => ({ ...prev, isOpen: false }))}
        />
      )}

      {confirmUnassignModal.isOpen && (
        <ConfirmModal
          isOpen={confirmUnassignModal.isOpen}
          message={confirmUnassignModal.message}
          confirmLabel="Sim, remover"
          onConfirm={confirmUnassignModal.onConfirm}
          onCancel={() => setConfirmUnassignModal((prev) => ({ ...prev, isOpen: false }))}
        />
      )}

      <ApproveSLAModal
        isOpen={slaModalCar !== null}
        car={slaModalCar}
        onClose={() => setSlaModalCar(null)}
        onUpdated={() => {
          fetchPendingSLACarsData();
          setActionsModalRefreshKey((k) => k + 1);
        }}
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona — Chefe de Oficina"
        secoes={helpSections}
      />
    </div>
  );
};

export default ChefeDeOficina;
